package com.dynamicruntime.common.cfact

import com.dynamicruntime.common.naming.OWNR
import com.dynamicruntime.common.naming.OwnedNameKind
import com.dynamicruntime.common.naming.isOwnedName

/**
 * A **cfact** is a named boolean fact about the current scope: present or absent, never a value (issue #454).
 *
 * The name is deliberately not "tag", which already means three unrelated things here -- the capability tags
 * that decide what a node carries, the publication mark on an endpoint, and the free-form search tags that
 * slice a catalog. A cfact is none of those: it is what a *display* decision is made against.
 *
 * **Presence only, and that is the rule that keeps this small.** A new condition is a new cfact computed in
 * Kotlin, never a richer operator here. "Has more than three forms" is a cfact called something like
 * `hasForms`, decided where the data is; it is not `count > 3` in an expression. Hold that line and the
 * evaluator stays a few dozen lines forever, instead of becoming a language nobody meant to write.
 *
 * **A cfact decides presentation, never permission.** Hiding a button must not be what stops the action --
 * the endpoint behind it refuses on its own, and a caller who calls it anyway is refused there. The same
 * distinction `publicApi` draws between advertising an endpoint and admitting a caller.
 */
interface CFactPredicate {
    /** Whether [cfacts] satisfies this. */
    fun matches(cfacts: Set<String>): Boolean

    /** The expression this came from, near enough to round-trip; for diagnostics and tests. */
    fun render(): String
}

/** One cfact by name: true when it is present. */
class CFactAtom(val name: String) : CFactPredicate {
    override fun matches(cfacts: Set<String>): Boolean = name in cfacts
    override fun render(): String = name
}

/**
 * The absence of [inner].
 *
 * **Prefer a positive expression wherever one exists.** `app` on the items an application shows says what is
 * meant; `~edge` says it by exclusion and quietly admits every role invented later. Negation is a last resort
 * rather than a peer of the others -- which is also why an unregistered name is refused at parse time: a
 * mistyped `~admn` names a cfact that is never present, so it is *always true*, and the guarded item shows to
 * everyone. The positive form of the same typo fails closed and is noticed at once.
 */
class CFactNot(val inner: CFactPredicate) : CFactPredicate {
    override fun matches(cfacts: Set<String>): Boolean = !inner.matches(cfacts)
    override fun render(): String = "~" + inner.render()
}

/** Every part must match. Written with `,`. */
class CFactAll(val parts: List<CFactPredicate>) : CFactPredicate {
    override fun matches(cfacts: Set<String>): Boolean = parts.all { it.matches(cfacts) }
    override fun render(): String = parts.joinToString(CFACT.and) { it.renderNested() }
}

/** Any part may match. Written with `|`. */
class CFactAny(val parts: List<CFactPredicate>) : CFactPredicate {
    override fun matches(cfacts: Set<String>): Boolean = parts.any { it.matches(cfacts) }
    override fun render(): String = parts.joinToString(CFACT.or) { it.renderNested() }
}

/** Parenthesizes a compound part when rendering it inside another, mirroring what the parser requires. */
private fun CFactPredicate.renderNested(): String =
    if (this is CFactAll || this is CFactAny) "(" + render() + ")" else render()

/** The expression syntax. Values are the characters themselves, so a message can quote them. */
@Suppress("ConstPropertyName")
object CFACT {
    /** Joins operands that must all match. */
    const val and = ","

    /** Joins operands of which any may match. */
    const val or = "|"

    /** Negates the operand that follows. */
    const val not = "~"

    const val open = "("
    const val close = ")"

    /**
     * Marks a **literal** rather than a cfact name.
     *
     * The same instinct as `SCH.gPrefix` marking kd2's own JSON Schema keywords: a reader can see at a glance
     * that `#never` is not something a component registered. It also keeps the cfact namespace free of
     * reserved words -- a real cfact may still be called `never` -- and lets the parser tell the two apart
     * *lexically*, so a mistyped literal reports itself as one instead of sending the reader hunting for a
     * registration that was never the problem.
     */
    const val literal = "#"

    /** Literal: matches nothing, whatever is present. */
    const val neverName = "#never"

    /** Literal: matches everything. */
    const val alwaysName = "#always"

    /**
     * [expression] as written out: an omitted one is [alwaysName], which is what omitting it means. For code that
     * compares or names conditions, so that "no condition" and `#always` are one condition wherever they are read.
     */
    fun orAlways(expression: String?): String = expression ?: alwaysName

    /**
     * Matches everything. Written [alwaysName], or by omitting the expression entirely.
     *
     * Spelled explicitly as well as omittable because that is how an overlay **re-enables** something a layer
     * beneath it disabled: a base that says [neverName] is turned back on by an overlay saying [alwaysName].
     * With only the omitted form, the same intent would have to be written as a tautology.
     */
    val always: CFactPredicate = object : CFactPredicate {
        override fun matches(cfacts: Set<String>): Boolean = true
        override fun render(): String = alwaysName
    }

    /**
     * Matches nothing.
     *
     * Its real use is **removal through an overlay**. A client or boot-role overlay sets an item's expression
     * to this and the item stops appearing -- so merging never needs to learn to delete an array element. The
     * base stays one readable list of everything that exists, "why is this gone?" is answered by reading the
     * item rather than diffing overlays, and no overlay can delete something a later one expected to find.
     */
    val never: CFactPredicate = object : CFactPredicate {
        override fun matches(cfacts: Set<String>): Boolean = false
        override fun render(): String = neverName
    }
}

/**
 * Whether [c] may appear in a cfact name: a letter, a digit, `_`, `.`, or the `:` of a rooted name
 * (`kdr:isCta`, issue #921). A colon is unambiguous here: the grammar's operators are `,`, `|`, `~`, parentheses
 * and the `#` literal sigil.
 *
 * Shared by the parser (which reads a name off an expression) and by [CFactDef] (which refuses a declaration
 * an expression could never spell). One rule rather than two, because the failure of two would be a name that
 * registers and then cannot be referred to -- discovered only by whoever tried to write it.
 */
fun isCFactNameChar(c: Char): Boolean = c.isLetterOrDigit() || c == '_' || c == '.' || c == OWNR.rootSep

/**
 * Whether [name] is spellable as a cfact name: a bare local part, or a rooted `<root>:<local>` with exactly one
 * colon (issue #921) -- the owner-name rule, which [isCFactNameChar] admits the characters of.
 */
fun isCFactName(name: String): Boolean = isOwnedName(OwnedNameKind.cfact, name)

/**
 * Every cfact name this expression refers to -- its atoms, unfolded through the operators, with the `#always` /
 * `#never` literals contributing nothing (they name no cfact). For a boot check that each name a `g-visibleWhen`
 * uses is one the deployment actually delivers where the expression is evaluated (issue #564).
 */
fun CFactPredicate.referencedNames(): Set<String> = when (this) {
    is CFactAtom -> setOf(name)
    is CFactNot -> inner.referencedNames()
    is CFactAll -> parts.flatMapTo(LinkedHashSet()) { it.referencedNames() }
    is CFactAny -> parts.flatMapTo(LinkedHashSet()) { it.referencedNames() }
    else -> emptySet()
}
